import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AngularFireStorage } from '@angular/fire/storage';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { finalize } from 'rxjs/operators';
import { FirestoreService } from '../service/firestore.service';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.css']
})
export class ProfileComponent implements OnInit {

  profileForm: FormGroup;
  profile: any;
  status = 'show';
  previousUploadedPhotoURL: string = null;
  previousUploadedLogoURL: string = null;
  uploadedPhotoURL: string = null;
  uploadedLogoURL: string = null;
  uploadingProfile = false;
  uploadingLogo = false;
  showClinicInput = false;
  phonePattern: RegExp = /^[+]{1}[1]{1}[\s]*((\([0-9]{3}\))|[0-9]{3})[\s\-]?((\([0-9]{3}\))|[0-9]{3})[\s\-]?[0-9]{4}$/;
  constructor(
    private fb: FormBuilder,
    private firestoreService: FirestoreService,
    private messageService: MessageService,
    private storage: AngularFireStorage,
    private router: Router,
    private location: Location,
  ) { }

  ngOnInit(): void {
    this.profile = JSON.parse(localStorage.getItem('profile'));
    this.showClinicInput = (this.profile.designation === 'clinic') ? true : false;
    this.previousUploadedPhotoURL = this.profile?.photo;
    this.previousUploadedLogoURL = this.profile?.logo;
    this.uploadedPhotoURL = this.uploadedLogoURL = null;
    this.createForm();
    this.setValues();
  }

  createForm(): void {
    this.profileForm = this.fb.group({
      fname: [null, Validators.required],
      lname: [null, Validators.required],
      name: [null, Validators.required],
      phone: [null, Validators.compose([Validators.required, Validators.pattern(this.phonePattern)])],
      email: [null, Validators.required],
      desc: [null],
      address: [null],
      photo: [null],
      logo: [null],
    });
  }
  setValues(): void {
    this.profileForm.patchValue({
      fname: this.profile?.fname,
      lname: this.profile?.lname,
      phone: this.profile?.phone,
      email: this.profile?.email,
      desc: this.profile?.desc,
      address: this.profile?.address,
      photo: this.profile?.photo,
      logo: this.profile?.logo,
    });
    if (this.profile.designation === 'clinic') {
      this.profileForm.patchValue({ name: this.profile?.name });
    } else {
      this.profileForm.removeControl('name');
    }
  }

  onImageSelect(type: string, event): void {
    if (this.status !== 'edit') { return; }
    const n = Date.now();
    const file = event.target.files[0];
    if (
      file?.type === 'image/jpg' || file?.type === 'image/jpeg' || file?.type === 'image/png'
    ) {
      if (type === 'profile') {
        this.uploadingProfile = true;
      } else {
        this.uploadingLogo = true;
      }
      const filePath = (type === 'profile') ? `profilePhotos/${n}` : `logoPhotos/${n}`;
      const fileRef = this.storage.ref(filePath);
      const task = this.storage.upload(filePath, file);
      task.snapshotChanges().pipe(finalize(() => {
        const imgDownloadURL = fileRef.getDownloadURL();
        imgDownloadURL.subscribe(url => {
          if (url) {
            if (type === 'profile') {
              if (this.uploadedPhotoURL !== null) { this.deleteImageFromStorage(this.uploadedPhotoURL); }
              this.uploadedPhotoURL = url;
              this.uploadingProfile = false;
              this.profileForm.patchValue({ photo: this.uploadedPhotoURL });
            } else {
              if (this.uploadedLogoURL !== null) { this.deleteImageFromStorage(this.uploadedLogoURL); }
              this.uploadedLogoURL = url;
              this.uploadingLogo = false;
              this.profileForm.patchValue({ logo: this.uploadedLogoURL });
            }
          }
        });
      })).subscribe(url => { });
    } else {
      this.messageService.add({
        key: 'info',
        severity: 'error',
        summary: 'File type not accepted',
        detail: 'Only jpg, jpeg, png images are accepted'
      });
    }
  }
  deleteImageFromStorage(url: string): void {
    this.storage.storage.refFromURL(url).delete();
  }

  back(): void {
    this.location.back();
  }

  saveProfile(): void {
    if (this.profileForm.valid) {
      this.status = 'show';
      // tslint:disable-next-line: prefer-const

      if (this.profileForm.value.logo) { }
      // if(){}

      if (this.profile.designation === 'admin') {
        this.firestoreService.updateAdmin(this.profile?.id, {
          fname: this.profileForm.value.fname,
          lname: this.profileForm.value.lname,
          phone: this.profileForm.value.phone.replace(/[\s\(\)-]/g, ''),
          email: this.profileForm.value.email,
          desc: this.profileForm.value.desc,
          address: this.profileForm.value.address,
          photo: (this.profileForm.value.photo) ? this.profileForm.value.photo : null,
          logo: (this.profileForm.value.logo) ? this.profileForm.value.logo : null,
        }).then(res => {
          this.messageService.add({ key: 'info', severity: 'success', summary: 'Success', detail: 'Profile Updated .' });
          localStorage.setItem('profile', JSON.stringify({ id: this.profile?.id, designation: 'admin', ...this.profileForm.value }));
        });
      } else {
        this.firestoreService.updateClinic(this.profile?.id, {
          email: this.profileForm.value.email,
          desc: this.profileForm.value.desc,
          address: this.profileForm.value.address,
          name: this.profileForm.value.name,
          logo: (this.profileForm.value.logo) ? this.profileForm.value.logo : null,
        }
        ).then(res => {
          this.firestoreService.updateStaff(this.profile.staff_id, {
            fname: this.profileForm.value.fname,
            lname: this.profileForm.value.lname,
            phone: this.profileForm.value.phone.replace(/[\s\(\)-]/g, ''),
            photo: (this.profileForm.value.photo) ? this.profileForm.value.photo : null
          }).then(res => {
            this.messageService.add({ key: 'info', severity: 'success', summary: 'Success', detail: 'Profile Updated .' });
            localStorage.setItem('profile', JSON.stringify({ id: this.profile?.id, designation: 'clinic', ...this.profileForm.value }));
          })
        });
      }
      if (this.previousUploadedPhotoURL !== this.profileForm.value.photo) { this.deleteImageFromStorage(this.previousUploadedPhotoURL); }
      if (this.previousUploadedLogoURL !== this.profileForm.value.logo) { this.deleteImageFromStorage(this.previousUploadedLogoURL); }
      this.router.navigateByUrl('/content');
    } else {
      const controls = this.profileForm.controls;
      Object.keys(controls).forEach(field => {
        controls[field].markAsDirty();
      });
    }

  }

}
